import "./PlotCard.css";

import { MAP_DISPLAY_MODES, isResultsTab } from "../../constants";
import {
  selectAttributesIncludingExperimentsAndModalities,
  selectHasResults,
  selectMergedTraits,
} from "../../selectors/resultMap";
import {
  setSelectedAttribute,
  setSelectedPlotId,
  setSelectedTrait,
} from "../../actions/resultMap";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { FloatingCard } from "../floatingCard/FloatingCard";
import { ImageHistory } from "./ImageHistory";
import { PlotImage } from "../PlotImage/PlotImage";
import { PropTypes } from "prop-types";
import { PropertiesMasonry } from "../PropertiesMasonry/PropertiesMasonry";
import { useTimeoutFn } from "react-use";

export const PlotCard = ({ feature }) => {
  const [selectedTrait, trialDate, selectedAttribute, mapDisplayMode] =
    useSelector(({ resultMap }) => [
      resultMap.selectedTrait,
      resultMap.trial_date,
      resultMap.selectedAttribute,
      resultMap.mapDisplayMode,
    ]);
  const traits = useSelector(selectMergedTraits);
  const attributes = useSelector(
    selectAttributesIncludingExperimentsAndModalities
  );
  const isFieldBookMode = mapDisplayMode === MAP_DISPLAY_MODES.FIELD_BOOK;
  const hasResults = useSelector(selectHasResults);

  // Same as the table on the left: clicking a property picks it, a trait for
  // its gradient, an attribute for its colours (the reducer switches the
  // map). Only in the Results tab: Plot Map and Field Map show plots, not
  // colours, so a click there would change nothing on screen. Nor would a
  // trait on a site without results: its plots have no trait colouring.
  const canSelectProperty = (key) =>
    Boolean(attributes[key]) ||
    (hasResults && traits.some(({ technical_name }) => technical_name === key));
  const showProperty = (key) => {
    if (attributes[key]) {
      dispatch(setSelectedAttribute(key));
      return;
    }
    const trait = traits.find(({ technical_name }) => technical_name === key);
    if (trait) dispatch(setSelectedTrait(trait));
  };

  const [showHistory, setShowHistory] = useState(false);

  // 100ms delay before closing history
  const [isReadyToCloseHistory, cancelCloseHistory, resetCloseHistory] =
    useTimeoutFn(() => setShowHistory(false), 100);

  const closeHistory = useCallback(() => {
    if (isReadyToCloseHistory() === false) cancelCloseHistory();
    else resetCloseHistory();
  }, [cancelCloseHistory, isReadyToCloseHistory, resetCloseHistory]);

  const dispatch = useDispatch();

  return (
    <div className="powerdash-component plot-card gap-1">
      <span className="plot-card-title p-1 mb-1">{feature.displayId}</span>
      <div
        className="plot-card-close clickable pe-2"
        onClick={() => dispatch(setSelectedPlotId(null))}
      >
        <i className="fa-solid fa-xmark" />
      </div>
      <div
        className="plot-explorer"
        onMouseEnter={() => {
          cancelCloseHistory();
          setShowHistory(true);
        }}
        onMouseLeave={closeHistory}
      >
        <PlotImage
          date={trialDate}
          feature={feature}
          bottomRightContent={
            <span
              className={`font-style-italic informative history-text ${
                showHistory ? "active" : ""
              }`}
            >
              Plot history <i className="fa-solid fa-angle-right" />
            </span>
          }
        />
        {showHistory && (
          <FloatingCard hide={() => setShowHistory(false)}>
            <ImageHistory feature={feature} />
          </FloatingCard>
        )}
      </div>
      <PropertiesMasonry
        properties={feature.properties}
        highlightedProperty={
          isFieldBookMode ? selectedAttribute : selectedTrait.technical_name
        }
        onSelectProperty={
          isResultsTab(mapDisplayMode) ? showProperty : undefined
        }
        canSelectProperty={canSelectProperty}
      />
    </div>
  );
};

PlotCard.propTypes = {
  feature: PropTypes.object.isRequired,
};
